import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  Search,
  Bell,
  Settings,
  Zap,
  Wifi,
  Cpu,
  Sun,
  Moon
} from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import LanguageSwitcher from '../../common/LanguageSwitcher';

const AdminHeader = ({ onMenuClick, isScrolled }) => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [liveData, setLiveData] = useState({ visitors: 142, txPerMin: 23, sysLoad: 34, memUsage: 62 });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData({
        visitors: Math.floor(120 + Math.random() * 80),
        txPerMin: Math.floor(15 + Math.random() * 25),
        sysLoad: Math.floor(20 + Math.random() * 40),
        memUsage: Math.floor(45 + Math.random() * 30),
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.header
      className={`sticky top-0 z-30 transition-all duration-300 ${
        isScrolled
          ? `${isDarkMode ? 'bg-gray-900/80' : 'bg-white/80'} backdrop-blur-xl shadow-sm border-b ${isDarkMode ? 'border-gray-700/20' : 'border-gray-200/20'}`
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div className="px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Lado izquierdo */}
          <div className="flex items-center space-x-4">
            <button
              onClick={onMenuClick}
              className={`p-2 hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-xl transition-colors`}
            >
              <Menu className={`w-6 h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
            
            {/* Búsqueda */}
            <div className="relative hidden md:block">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} w-5 h-5`} />
              <input
                type="text"
                placeholder={t('common.search')}
                className={`pl-10 pr-4 py-2.5 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm border ${isDarkMode ? 'border-gray-600/50' : 'border-gray-200/50'} rounded-2xl w-80 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500/50 transition-all ${isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-900'}`}
              />
            </div>
          </div>

          {/* Lado derecho */}
          <div className="flex items-center space-x-4">
            {/* Indicadores en tiempo real */}
            <div className="hidden lg:flex items-center space-x-3">
              <motion.div
                className={`flex items-center space-x-2 px-3 py-2 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm rounded-2xl border ${isDarkMode ? 'border-gray-600/50' : 'border-gray-200/50'}`}
                whileHover={{ scale: 1.05 }}
              >
                <div className="relative">
                  <Wifi className="w-4 h-4 text-green-500" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full"></div>
                </div>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {liveData.visitors}
                </span>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('common.online')}</span>
              </motion.div>

              <motion.div
                className={`flex items-center space-x-2 px-3 py-2 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm rounded-2xl border ${isDarkMode ? 'border-gray-600/50' : 'border-gray-200/50'}`}
                whileHover={{ scale: 1.05 }}
              >
                <Zap className="w-4 h-4 text-orange-500" />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {liveData.txPerMin}/min
                </span>
              </motion.div>

              <motion.div
                className={`flex items-center space-x-2 px-3 py-2 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/50'} backdrop-blur-sm rounded-2xl border ${isDarkMode ? 'border-gray-600/50' : 'border-gray-200/50'}`}
                whileHover={{ scale: 1.05 }}
              >
                <Cpu className="w-4 h-4 text-orange-500" />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {liveData.sysLoad}%
                </span>
              </motion.div>
            </div>

            {/* Hora actual */}
            <div className={`hidden sm:block text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {currentTime.toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </div>

            {/* Language switcher and action icons */}
            <div className="flex items-center space-x-3">
              <LanguageSwitcher />

              <div className="flex items-center space-x-1">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`p-2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} rounded-xl transition-colors relative`}
                >
                  <Bell className="w-5 h-5" />
                  <div className={`absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 ${isDarkMode ? 'border-gray-900' : 'border-white'}`}></div>
                </motion.button>

                <motion.button
                  onClick={toggleTheme}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`p-2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} rounded-xl transition-colors`}
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`p-2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} rounded-xl transition-colors`}
                >
                  <Settings className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default AdminHeader;